import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, Form, Button, Pagination, Container, Row, Col, Card, Modal, Spinner, Alert 
} from 'react-bootstrap';
import axios from 'axios';

const UsersPage = () => {
    const userData = JSON.parse(sessionStorage.getItem("userData")) || {};
  const API_BASE = process.env.REACT_APP_COLLEGE_ERP_API;  
  const currentBranch = "Demo"; // Make sure this matches your dynamic branch setup

  // State Management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Search & Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // Change display limit sizes here

  // Edit Modal State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    userName: '',
    mobileNo: '',
    emailId: '',
    rights: '',
    address: '',
    dob: '',
    password: ''
  });
 
const [totalItems, setTotalItems] = useState(0); // <-- ADD THIS LINE
 

  // Fetch Users Function
 const fetchUsers = useCallback(async () => {
  setLoading(true);
  setError(null);
  try {
    const response = await axios.get(`${API_BASE}users`, {
      params: { 
        branch: userData.branch,
        page: currentPage,    // Passes active page index to backend
        limit: itemsPerPage   // Matches UI rendering constraint
      },
      headers: {
        Authorization: `Bearer ${userData.token}`
      }
    });
    
    // Server now returns an object with a users array and a total count
    setUsers(response.data.users || []);
    setTotalItems(response.data.total || 0); 
  } catch (err) {
    setError("Failed to load users from repository.");
  } finally {
    setLoading(false);
  }
}, [API_BASE, userData.branch, currentPage]); // Triggers reload every time currentPage changes

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Handle Search Input Form Actions
  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) {
      fetchUsers();
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}searchUsers`, {
        params: { branch: currentBranch, search: searchTerm }
      });
      setUsers(Array.isArray(response.data) ? response.data : []);
      setCurrentPage(1); // Drop index back to page 1 on active searching changes
    } catch (err) {
      setError("Active database backend search indexing failed.");
    } finally {
      setLoading(false);
    }
  };

  // Open Edit Dialog Box
  const handleEditClick = (user) => {
    setEditingUser(user);
    setEditFormData({
      userName: user.userName || '',
      mobileNo: user.mobileNo || '',
      emailId: user.emailId || '',
      rights: user.rights || '',
      address: user.address || '',
      dob: user.dob || '',
      password: '' // Keep secure blank initialization
    });
    setShowEditModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  // Submit Changed Values to update-credentials Endpoint
  const handleSaveChanges = async (e) => {
    e.preventDefault();
    setSuccessMsg(null);
    setError(null);

    const payload = { ...editFormData };
    if (!payload.password) delete payload.password; // Do not overwrite existing pass hashes if empty

    try {
      // Direct integration hook to update-credentials route assignment
      const url = `${API_BASE}update-credentials?branch=${userData.branch}&id=${editingUser._id}`;
      await axios.post(url, payload);

      setSuccessMsg(`User '${editingUser.userName}' database values updated successfully.`);
      setShowEditModal(false);
      fetchUsers(); // Live reload matching records context
    } catch (err) {
      setError(err.response?.data?.error || "Error executing requested credential updates.");
    }
  };

  // Client Side UI Filters Logic Combo
  const filteredUsers = users.filter(user => {
    if (roleFilter === 'All') return true;
    return user.rights?.toLowerCase() === roleFilter.toLowerCase();
  });

  // Client Pagination Calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  return (
    <Container className="mt-4">
      <Card className="shadow-sm border-0">
        <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">ERP User Administration Panel</h5>
          <Button variant="outline-light" size="sm" onClick={fetchUsers}>Sync Records</Button>
        </Card.Header>
        <Card.Body>
          
          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
          {successMsg && <Alert variant="success" dismissible onClose={() => setSuccessMsg(null)}>{successMsg}</Alert>}

          {/* Search controls + Dropdown Role filters */}
          <Form onSubmit={handleSearchSubmit} className="mb-4">
            <Row className="g-2">
              <Col md={6}>
                <Form.Control
                  type="text"
                  placeholder="Query matching IDs, names, or emails..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>
              <Col md={3}>
                <Form.Select 
                  value={roleFilter} 
                  onChange={(e) => { setRoleFilter(e.target.value); setCurrentPage(1); }}
                >
                  <option value="All">All User Roles</option>
                  <option value="Subscriber">Subscriber</option>
                  <option value="Admin">Admin</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Button type="submit" variant="primary" className="w-100">Filter Search</Button>
              </Col>
            </Row>
          </Form>

          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" variant="dark" />
            </div>
          ) : (
            <>
              <Table responsive striped hover className="align-middle border">
                <thead className="table-light">
                  <tr>
                    <th>User Login String</th>
                    <th>User Name</th>
                    <th>Email Field</th>
                    <th>System Role</th>
                    <th>DOB</th>
                    <th className="text-center">Modify</th>
                  </tr>
                </thead>
              <tbody>
  {currentUsers && currentUsers.length > 0 ? (
    currentUsers.map((user) => (
      <tr key={user._id}>
        <td><small className="text-muted font-monospace">{user.id}</small></td>
        {/* Handles both userName and the useName typo gracefully */}
        <td><strong>{user.userName || user.useName || 'N/A'}</strong></td>
        <td>{user.emailId || <span className="text-muted">No Email</span>}</td>
        <td>
          <span className={`badge ${user.rights === 'Subscriber' ? 'bg-secondary' : 'bg-success'}`}>
            {user.rights || 'No Rights'}
          </span>
        </td>
        <td>{user.dob || <em className="text-muted">Unset</em>}</td>
        <td className="text-center">
          <Button variant="outline-warning" size="sm" onClick={() => handleEditClick(user)}>
            Edit
          </Button>
        </td>
      </tr>
    ))
  ) : (
    <tr>
      <td colSpan="6" className="text-center py-4 text-muted">No records match filter bounds.</td>
    </tr>
  )}
</tbody>
              </Table>

              {/* Functional Pagination List */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <span className="text-muted small">
                    Showing entries {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredUsers.length)} of {filteredUsers.length}
                  </span>
                  <Pagination className="mb-0">
                    <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                    {[...Array(totalPages).keys()].map(number => (
                      <Pagination.Item 
                        key={number + 1} 
                        active={number + 1 === currentPage}
                        onClick={() => setCurrentPage(number + 1)}
                      >
                        {number + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Edit Form Modal Block popup */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Modify Core Registry Credentials</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveChanges}>
          <Modal.Body>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Label>User Public Name</Form.Label>
                <Form.Control
                  type="text"
                  name="userName"
                  value={editFormData.userName}
                  onChange={handleInputChange}
                  required
                />
              </Col>
             <Col md={6} className="mb-3">
  <Form.Label>Assigned Role Level Permission</Form.Label>
  <Form.Select
    name="rights"
    value={editFormData.rights}
    onChange={handleInputChange}
    required
  >
    <option value="" disabled>-- Select a Role --</option>
    <option value="Admin">Admin</option>
    <option value="Subscriber">Subscriber</option>
    <option value="SuperAdmin">SuperAdmin</option>
  </Form.Select>
</Col>
            </Row>

            <Row>
              <Col md={6} className="mb-3">
                <Form.Label>Contact Mobile Number</Form.Label>
                <Form.Control
                  type="text"
                  name="mobileNo"
                  placeholder="No phone profile listed"
                  value={editFormData.mobileNo}
                  onChange={handleInputChange}
                />
              </Col>
              <Col md={6} className="mb-3">
                <Form.Label>Primary Email Address Identity</Form.Label>
                <Form.Control
                  type="email"
                  name="emailId"
                  value={editFormData.emailId}
                  onChange={handleInputChange}
                />
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Registered Mailing Address Location</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="address"
                value={editFormData.address}
                onChange={handleInputChange}
              />
            </Form.Group>

            <Row>
              <Col md={6} className="mb-3">
                <Form.Label>Date of Birth Calendar Entry</Form.Label>
                <Form.Control
                  type="date"
                  name="dob"
                  value={editFormData.dob}
                  onChange={handleInputChange}
                />
              </Col>
              <Col md={6} className="mb-3">
                <Form.Label>Change Entry Password (Leave empty to keep current)</Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  placeholder="Insert new secure text sequence if updating..."
                  value={editFormData.password}
                  onChange={handleInputChange}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setShowEditModal(false)}>Close</Button>
            <Button type="submit" variant="dark">Apply Database Adjustments</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default UsersPage;